import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ChevronDown, ShoppingBag, Mail } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import Seo from "@/components/Seo";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");
  const { addItem } = useCart();

  const minDate = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
  const TIME_WINDOWS = ["9:00 AM – 12:00 PM", "12:00 PM – 3:00 PM", "3:00 PM – 6:00 PM"];

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${API}/products/${id}`);
        setProduct(res.data);
      } catch (e) {
        console.error(e);
        toast.error("Product not found");
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const getCurrentPrice = () => {
    if (!product) return 0;
    if (product.has_variants && product.variants && product.variants.length > 0) {
      return product.variants[selectedVariantIndex]?.price || product.price;
    }
    return product.price;
  };

  const handleAddToCart = () => {
    if (!product) return;
    if (!deliveryDate || !deliveryTime) {
      toast.error("Please choose a delivery date and time.");
      return;
    }
    const variantIdx = (product.has_variants && product.variants) ? selectedVariantIndex : null;
    addItem(product, variantIdx, { date: deliveryDate, time: deliveryTime });
    toast.success(`${product.name} added to cart`);
  };

  if (loading) {
    return (
      <div className="pt-[72px] min-h-screen bg-[#0c0c0c] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#b8966a]" size={32} />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-[72px] min-h-screen bg-[#0c0c0c] flex items-center justify-center">
        <div className="text-center">
          <p className="font-body text-[#a19d98] mb-4">Product not found</p>
          <Link to="/collection" className="btn-secondary">Back to Collection</Link>
        </div>
      </div>
    );
  }

  const isInquiryOnly = product.inquiry_only === true;
  const canonicalPath = `/collection/${product.id}`;
  const canonicalUrl = `https://fleurisnoir.com${canonicalPath}`;
  const availability = product.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock";
  const offers = (product.has_variants && product.variants && product.variants.length)
    ? product.variants.map((v) => ({
        "@type": "Offer", name: v.label, price: String(v.price), priceCurrency: "USD",
        availability, url: canonicalUrl,
      }))
    : { "@type": "Offer", price: String(product.price), priceCurrency: "USD", availability, url: canonicalUrl };
  const productLd = {
    "@context": "https://schema.org", "@type": "Product",
    name: product.name, description: product.description, image: product.image_url,
    brand: { "@type": "Brand", name: "Fleuris Noir" }, offers,
  };
  const breadcrumbLd = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://fleurisnoir.com/" },
      { "@type": "ListItem", position: 2, name: "Collection", item: "https://fleurisnoir.com/collection" },
      { "@type": "ListItem", position: 3, name: product.name, item: canonicalUrl },
    ],
  };

  return (
    <div className="page-transition pt-[72px] min-h-screen bg-[#0c0c0c]" data-testid="product-detail-page">
      <Seo
        title={`${product.name} | Fleuris Noir`}
        description={(product.description || `${product.name} by Fleuris Noir — luxury florist in Minneapolis.`).slice(0, 155)}
        path={canonicalPath}
        image={product.image_url}
        ogType="product"
        jsonLd={[productLd, breadcrumbLd]}
      />
      <div className="container-custom py-12">
        <nav aria-label="Breadcrumb" className="mb-6 font-sans text-xs tracking-[0.15em] uppercase text-[#a19d98]" data-testid="product-breadcrumb">
          <Link to="/" className="hover:text-[#b8966a] transition-colors">Home</Link>
          <span className="mx-2 text-[#b8966a]/50">/</span>
          <Link to="/collection" className="hover:text-[#b8966a] transition-colors">Collection</Link>
          <span className="mx-2 text-[#b8966a]/50">/</span>
          <span className="text-[#f8f5f0]">{product.name}</span>
        </nav>
        <Link
          to="/collection"
          className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.15em] uppercase text-[#a19d98] hover:text-[#b8966a] transition-colors mb-8"
          data-testid="back-to-collection"
        >
          <ArrowLeft size={16} />
          Back to Collection
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image */}
          <div className="aspect-[3/4] overflow-hidden">
            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" data-testid="product-image" />
          </div>

          {/* Details */}
          <div className="flex flex-col justify-center">
            <p className="overline mb-4">
              {product.category === "event" ? "Event Package" : product.category === "bouquet" ? "Signature Bouquet" : product.category === "arrangement" ? "Floral Arrangement" : product.category}
            </p>
            <h1 className="font-display text-3xl md:text-4xl text-[#f8f5f0] mb-4" data-testid="product-name">
              {product.name}
            </h1>
            <p className="font-body text-lg text-[#a19d98] mb-8 leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            {product.tagline && (
              <p className="font-heading text-lg italic text-[#b8966a]/80 mb-6">"{product.tagline}"</p>
            )}

            {product.details && product.details.length > 0 && (
              <ul className="space-y-2.5 mb-8" data-testid="product-details">
                {product.details.map((d) => (
                  <li key={d} className="flex items-start gap-3 font-body text-sm text-[#a19d98]">
                    <span className="text-[#b8966a] mt-0.5 flex-shrink-0">•</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* Variant Selector */}
            {product.has_variants && product.variants && product.variants.length > 0 && (
              <div className="mb-6">
                <p className="overline mb-4">Select Size</p>
                <div className="relative">
                  <select
                    value={selectedVariantIndex}
                    onChange={(e) => setSelectedVariantIndex(Number(e.target.value))}
                    className="w-full bg-[#161616] border border-[#b8966a]/30 text-[#f8f5f0] py-4 px-5 pr-12 font-body text-lg appearance-none cursor-pointer hover:border-[#b8966a]/60 focus:border-[#b8966a] transition-colors"
                    data-testid="variant-selector"
                  >
                    {product.variants.map((variant, index) => (
                      <option key={variant.label} value={index} className="bg-[#161616] text-[#f8f5f0]">
                        {variant.label} — ${variant.price.toFixed(2)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#b8966a] pointer-events-none" />
                </div>
                {/* Selected size description */}
                {product.variants[selectedVariantIndex]?.description && (
                  <p className="font-body text-sm text-[#a19d98] mt-3 leading-relaxed">
                    {product.variants[selectedVariantIndex].description}
                  </p>
                )}
                {/* Add-ons note for Rosé */}
                {product.name === "Rosé Classique" && (
                  <p className="font-body text-sm text-[#b8966a] mt-3">Add-ons available for $15.00</p>
                )}
              </div>
            )}

            {/* Delivery Date & Time — required for all purchasable bouquets */}
            {!isInquiryOnly && (
              <div className="mb-6 grid sm:grid-cols-2 gap-4" data-testid="delivery-selection">
                <div>
                  <p className="overline mb-3">Delivery Date</p>
                  <input
                    type="date"
                    min={minDate}
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    className="w-full bg-[#161616] border border-[#b8966a]/30 text-[#f8f5f0] py-4 px-5 font-body cursor-pointer hover:border-[#b8966a]/60 focus:border-[#b8966a] transition-colors"
                    data-testid="delivery-date-input"
                  />
                </div>
                <div>
                  <p className="overline mb-3">Delivery Time</p>
                  <div className="relative">
                    <select
                      value={deliveryTime}
                      onChange={(e) => setDeliveryTime(e.target.value)}
                      className="w-full bg-[#161616] border border-[#b8966a]/30 text-[#f8f5f0] py-4 px-5 pr-12 font-body appearance-none cursor-pointer hover:border-[#b8966a]/60 focus:border-[#b8966a] transition-colors"
                      data-testid="delivery-time-input"
                    >
                      <option value="" disabled>Select a window</option>
                      {TIME_WINDOWS.map((t) => (
                        <option key={t} value={t} className="bg-[#161616]">{t}</option>
                      ))}
                    </select>
                    <ChevronDown size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#b8966a] pointer-events-none" />
                  </div>
                </div>
              </div>
            )}

            <div className="border-t border-b border-[#b8966a]/10 py-6 mb-8">
              <p className="font-sans text-2xl text-[#b8966a]" data-testid="product-price">
                {isInquiryOnly ? `Starting at $${product.price?.toFixed(2)}` : `$${getCurrentPrice().toFixed(2)}`}
              </p>
              <p className="font-body text-sm text-[#a19d98] mt-1">
                {isInquiryOnly ? "Custom pricing — inquire for details" : product.in_stock ? "In Stock" : "Out of Stock"}
              </p>
            </div>

            {/* Action Buttons */}
            {isInquiryOnly ? (
              <Link
                to="/inquiries"
                className="btn-primary flex items-center justify-center gap-2"
                data-testid="inquiry-button"
              >
                <Mail size={16} />
                Inquire About This Package
              </Link>
            ) : (
              <button
                onClick={handleAddToCart}
                disabled={!product.in_stock}
                className="btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="add-to-cart-button"
              >
                <ShoppingBag size={16} />
                Add to Cart
              </button>
            )}

            {/* Additional Info */}
            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-1 h-1 bg-[#b8966a] rounded-full mt-2" />
                <p className="font-body text-sm text-[#a19d98]">Orders must be placed at least 72 hours in advance</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1 h-1 bg-[#b8966a] rounded-full mt-2" />
                <p className="font-body text-sm text-[#a19d98]">Local delivery available within Minneapolis</p>
              </div>
              {isInquiryOnly && (
                <div className="flex items-start gap-3">
                  <div className="w-1 h-1 bg-[#b8966a] rounded-full mt-2" />
                  <p className="font-body text-sm text-[#a19d98]">Full consultation included — we'll design together</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
